import React from "react";
import clsx from "clsx";

import { ReactComponent as IconArrow } from "../../../assets/svg/caret_down.svg";
import { ReactComponent as IconCheck } from "../../../assets/svg/check-circle.svg";
import { ReactComponent as IconStudent } from "../../../assets/svg/student-comment.svg";
import {
  IAnswersByClass,
  IStudent,
  IClass,
  IGroup,
  ITeacherR,
  IAnswersFromOtherSchoolByClass,
  IAnswer,
} from "../../../state";
import { IAnswersByGroup } from "../../../pages/teacher/types";
import { generateFullName } from "../../../libs/Util";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { Context } from "../../../teacherStore";
import SwitchBoxAnswerPage from "../../../molecules/teacher/SwitchBoxAnswerPage";
import AnswerBox from "../../../molecules/teacher/AnswerBox";
import OneAnswerContents from "../../../molecules/OneAnswerContents";
import Dropdown, { Option } from "../../../atoms/Dropdown";

import styles from "./AnswerList.module.scss";

export type AnswerProps = {
  calling: boolean;
  currentTeacherID: number;
  students: IStudent[];
  answersByClasses: IAnswersByClass[];
  answersOtherSchoolsByClasses: IAnswersFromOtherSchoolByClass[];
  classes: IClass[];
  classIDs: number[];
  groups: IGroup[];
  is_display_all_schools_answer: boolean;
  handleComment: (
    content: string,
    answerID: number,
    comment_id?: number,
  ) => void;
  handleDeleteComment: (answer_id: number, comment_id: number) => void;
  handleToggleComment: (
    answer_id: number,
    comment_id: number,
    is_hidden: boolean,
  ) => void;
  changeAnswerOpenClose: (class_id: number, opened: boolean) => void;
  changeNameOpenClose: (class_id: number, opened: boolean) => void;
};

const options: Option[] = [
  { label: "回答の新しい順", value: 1 },
  { label: "文字数の多い順", value: 2 },
  { label: "いいね数の多い順", value: 3 },
];

const getContentsLength = (answer: IAnswer) =>
  answer.contents?.map(c => c.content.length).reduce((p, c) => p + c) ?? 0;

const AnswerList: React.FC<AnswerProps> = ({
  calling,
  currentTeacherID,
  students,
  answersByClasses,
  answersOtherSchoolsByClasses,
  classes,
  classIDs,
  groups,
  is_display_all_schools_answer,
  handleComment,
  handleDeleteComment,
  handleToggleComment,
  changeAnswerOpenClose,
  changeNameOpenClose,
}) => {
  const { state } = React.useContext(Context);
  const scrollRefs = React.useRef<any[]>([]);
  const [openModal, setOpenModal] = React.useState<{
    state: boolean;
    type: "delete" | "hide";
    answerId: number;
    commentId: number;
    hiddenBy?: null | ITeacherR;
  }>({
    state: false,
    type: "delete",
    answerId: 0,
    commentId: 0,
  });
  const [listStudent, setListStudent] = React.useState<{
    [key: number]: IStudent[];
  }>({});

  const [sortType, setSortType] = React.useState(options[0]);

  const answersByStudents = React.useMemo(() => {
    const all = answersByClasses
      .map(k => k.answers.map(a => a.student.id))
      .flat();
    const studentIds = Array.from(new Set([...all]));
    return studentIds.map(studentId => {
      const klass = answersByClasses.find(k =>
        k.answers.find(a => a.student.id === studentId),
      )!;
      const answer = klass.answers.find(a => a.student.id === studentId)!;

      return {
        student_id: studentId,
        name_opened: klass.name_opened,
        answers: [answer],
        classFullName: klass.full_name,
      };
    });
  }, [answersByClasses]);

  const sortedAnswersByStudents = React.useMemo(() => {
    switch (sortType.value) {
      case 2:
        // Sort by contents-length desc
        return [...answersByStudents].sort(
          (x, y) =>
            getContentsLength(y.answers[0]) - getContentsLength(x.answers[0]),
        );
      case 3:
        // Sort by like_num desc
        return [...answersByStudents].sort(
          (x, y) => y.answers[0].like_num - x.answers[0].like_num,
        );
      default:
        // Sort by id desc
        return [...answersByStudents].sort(
          (x, y) => y.answers[0].id - x.answers[0].id,
        );
    }
  }, [answersByStudents, sortType]);

  const answersByGroups: IAnswersByGroup[] = React.useMemo(
    () =>
      groups.map(group => {
        const abs = answersByStudents.filter(st =>
          st.answers.find(a => a.student.group?.id === group.id),
        );
        return {
          ...group,
          name_opened: abs.some(st => st.name_opened),
          studentAnswers: abs.map(st => st.answers),
        };
      }),
    [groups, answersByStudents],
  );

  React.useEffect(() => {
    if (state.success_message) {
      setOpenModal({
        state: false,
        type: "delete",
        answerId: 0,
        commentId: 0,
      });
    }
  }, [state.success_message]);

  React.useEffect(() => {
    const studentMap = {} as { [key: number]: IStudent[] };
    const studentsSortedByName = students.sort((a, b) => {
      return a.last_name_kana.localeCompare(b.last_name_kana, "ja");
    });
    studentsSortedByName.forEach(student => {
      studentMap[student.class_id]
        ? studentMap[student.class_id].push(student)
        : (studentMap[student.class_id] = [student]);
    });
    setListStudent(studentMap);
  }, [students]);

  const scrollSmoothHandler = (index: number) => {
    scrollRefs.current[index].current.scrollIntoView({ behavior: "smooth" });
  };

  sortedAnswersByStudents.forEach((_, i) => {
    scrollRefs.current[i] = React.createRef();
  });

  const handleClickStudent = (student_id: number) => {
    const index = sortedAnswersByStudents.findIndex(
      st => st.student_id === student_id,
    );
    if (index >= 0) scrollSmoothHandler(index);
  };

  const isShowClassFullName = answersByClasses.length > 1;

  return (
    <div className="d-flex h-100">
      <div className={clsx(styles.wrapper, styles.wrapper__modal)}>
        <div className="mt-4">
          <ul
            className={clsx(
              `nav nav-pills mb-3`,
              styles.tabs,
              is_display_all_schools_answer && styles.otherSchool,
            )}
            id="pills-tab"
            role="tablist"
          >
            <li className="nav-item">
              <a
                className="active"
                data-bs-toggle="pill"
                href="#pills-class"
                role="tab"
                aria-controls="pills-class"
                aria-selected="true"
              >
                クラスの回答
              </a>
            </li>
            <li className="nav-item">
              <a
                data-bs-toggle="pill"
                href="#pills-group"
                role="tab"
                aria-controls="pills-group"
                aria-selected="false"
              >
                グループの回答
              </a>
            </li>
            {is_display_all_schools_answer && (
              <li className="nav-item">
                <a
                  data-bs-toggle="pill"
                  href="#pills-other-school"
                  role="tab"
                  aria-controls="pills-other-school"
                  aria-selected="false"
                >
                  他校の回答
                </a>
              </li>
            )}
          </ul>
          <div className="tab-content" id="pills-tabContent">
            <div
              className={`tab-pane fade show active ${styles.tabClass}`}
              id="pills-class"
              role="tabpanel"
              aria-labelledby="pills-class-tab"
            >
              {classIDs.map(clId => {
                const answersByClass = answersByClasses.find(
                  k => k.id === clId,
                );
                return (
                  <div className={styles.groupClass} key={clId}>
                    <div className={styles.className}>
                      <h5>{classes.find(cls => clId === cls.id)?.full_name}</h5>
                      <div
                        data-bs-toggle="collapse"
                        aria-expanded="true"
                        data-bs-target={`#listStudent${clId}`}
                      >
                        <IconArrow />
                      </div>
                    </div>
                    <div className="collapse show" id={`listStudent${clId}`}>
                      <div className={styles.tabClass__student}>
                        {answersByClass?.name_opened
                          ? listStudent[clId]?.map(
                              student =>
                                student.class_id === clId && (
                                  <div
                                    key={student.id}
                                    data-bs-tooltip={`${generateFullName([
                                      student.last_name,
                                      student.first_name,
                                    ])}\n${generateFullName([
                                      student.last_name_kana,
                                      student.first_name_kana,
                                    ])}`}
                                    onClick={() =>
                                      handleClickStudent(student.id)
                                    }
                                  >
                                    {answersByClass.answers.find(
                                      answer =>
                                        answer.student.id === student.id,
                                    ) && <IconCheck />}
                                    <p>
                                      {generateFullName([
                                        student.last_name,
                                        student.first_name,
                                      ])}
                                    </p>
                                    <span>
                                      {generateFullName([
                                        student.last_name_kana,
                                        student.first_name_kana,
                                      ])}
                                    </span>
                                  </div>
                                ),
                            )
                          : answersByClass?.answers.map((a, index) => (
                              <div
                                key={index}
                                onClick={() => handleClickStudent(a.student.id)}
                              >
                                <p>回答{a.index}</p>
                              </div>
                            ))}
                      </div>
                      {answersByClass && (
                        <div className={styles.tabClass__actions}>
                          <SwitchBoxAnswerPage
                            label="みんなの回答を公開"
                            type="answer_opened"
                            answersByClass={answersByClass}
                            onChange={changeAnswerOpenClose}
                          />
                          <SwitchBoxAnswerPage
                            label="生徒名をクラスに公開"
                            type="name_opened"
                            answersByClass={answersByClass}
                            onChange={changeNameOpenClose}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="d-flex align-items-center justify-content-end mt-3">
                <Dropdown
                  className={styles.dropdown}
                  options={options}
                  defaultValue={sortType}
                  onChange={value => {
                    const selectSort = options.find(
                      option => option.value === value,
                    ) as Option;
                    setSortType(selectSort);
                  }}
                />
              </div>
              {sortedAnswersByStudents.map((st, index) => (
                <AnswerBox
                  key={`answer${st.student_id}`}
                  calling={calling}
                  studentAnswers={st.answers}
                  currentTeacherID={currentTeacherID}
                  name_opend={st.name_opened}
                  classFullName={st.classFullName}
                  isShowClassFullName={isShowClassFullName}
                  handleComment={handleComment}
                  handleToggleComment={handleToggleComment}
                  handleDeleteComment={handleDeleteComment}
                  ref={scrollRefs.current[index]}
                />
              ))}
            </div>
            <div
              className="tab-pane fade"
              id="pills-group"
              role="tabpanel"
              aria-labelledby="pills-group-tab"
            >
              {answersByGroups.map(group => (
                <div className={styles.tabGroup} key={group.id}>
                  <button
                    className={styles.tabGroup__label}
                    data-bs-toggle="collapse"
                    data-bs-target={`#group${group.id}`}
                    aria-expanded="false"
                    aria-controls={`group${group.id}`}
                  >
                    <span>{group.name}</span>
                    <IconArrow />
                  </button>
                  <div className="collapse" id={`group${group.id}`}>
                    {group.studentAnswers.length > 0 ? (
                      group.studentAnswers.map((answers, index) => (
                        <AnswerBox
                          key={`groupAnswer${index}`}
                          isGroupAnswer
                          calling={calling}
                          studentAnswers={answers}
                          currentTeacherID={currentTeacherID}
                          name_opend={group.name_opened}
                          handleComment={handleComment}
                          handleToggleComment={handleToggleComment}
                          handleDeleteComment={handleDeleteComment}
                        />
                      ))
                    ) : (
                      <p className={styles.tabGroup__nothing}>
                        回答がありません
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div
              className="tab-pane fade"
              id="pills-other-school"
              role="tabpanel"
              aria-labelledby="pills-group-tab"
            >
              {answersOtherSchoolsByClasses.map(cl =>
                cl.answers.map(answer => (
                  <div className={styles.answerFromOtherSchool} key={answer.id}>
                    <h5>
                      <IconStudent />
                      {cl.grade >= 1 && cl.grade <= 3
                        ? `${cl.grade}年生`
                        : "その他"}
                    </h5>
                    <div className={styles.wrapper_content}>
                      <OneAnswerContents contents={answer.contents} />
                    </div>
                  </div>
                )),
              )}
            </div>
          </div>
        </div>
      </div>
      {openModal.state && (
        <ModalDeleteConfirm
          title={
            openModal.type === "delete"
              ? "本当に削除しますか ?"
              : openModal.hiddenBy === null
              ? "このコメントを非表示にしてよろしいですか？"
              : "このコメントを再度表示してよろしいですか？"
          }
          onConfirm={() => {
            if (openModal.hiddenBy !== undefined) {
              handleToggleComment(
                openModal.answerId,
                openModal.commentId,
                !!!openModal.hiddenBy,
              );
            } else {
              handleDeleteComment(openModal.answerId, openModal.commentId);
            }
          }}
          onClose={() =>
            setOpenModal({
              state: false,
              type: "delete",
              answerId: 0,
              commentId: 0,
            })
          }
        />
      )}
    </div>
  );
};

export default AnswerList;
